import { useEffect, useMemo, useRef, useState } from 'react';
import { Menu, menuClasses, Sidebar as ProSidebar, sidebarClasses } from 'react-pro-sidebar';
import { Box, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { COLLAPSED_SIDEBAR_WIDTH, EXPANDED_SIDEBAR_WIDTH } from '@shared/constants/otherConstants';
import {
    UI_BOOKING_PATH,
    UI_DEFECTS_PATH,
    UI_FUNCTIONAL_TESTING_PATH,
    UI_LIBRARY_PATH,
    UI_MODULES_PATH,
    UI_SCHEMES_PATH,
    UI_STANDS_PATH,
    UI_TEST_CASE_PATH,
    UI_TEST_PLAN_PATH,
    UI_TEST_RUN_PATH,
} from '@shared/constants/pathsConstants';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import RuleOutlinedIcon from '@mui/icons-material/RuleOutlined';
import SubscriptionsOutlinedIcon from '@mui/icons-material/SubscriptionsOutlined';
// import PlaylistPlayOutlinedIcon from '@mui/icons-material/PlaylistPlayOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import MonitorOutlinedIcon from '@mui/icons-material/MonitorOutlined';
import { useTranslation } from 'react-i18next';
import SidebarRecursiveMenu from './SidebarRecursiveMenu';
import { PERMISSIONS } from '@app/permissions/permissions';
import { EXECUTION_DASHBOARD_ROUTE } from '@shared/constants/routesConstants';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { selectIsSidebarCollapseBlocked, selectIsSidebarCollapsed } from '@features/favorites/store/selectors';
import BugReportIcon from '@mui/icons-material/BugReport';

export const Sidebar = () => {
    const theme = useTheme();
    const isCollapsed = useSelector(selectIsSidebarCollapsed);
    const [isVisibleCollapsed, setIsVisibleCollapsed] = useState(isCollapsed);
    const isSidebarCollapseBlocked = useSelector(selectIsSidebarCollapseBlocked);
    const collapseTimeout = useRef(null);
    const { t } = useTranslation();

    const menuItems = useMemo(() => {
        return [
            {
                label: t('dashboards'),
                icon: <BarChartOutlinedIcon sx={{ fill: 0 }} />,
                link: EXECUTION_DASHBOARD_ROUTE,
                permission: PERMISSIONS.VIEW_DASHBOARDS,
                testId: 'sidebar_dashboards',
            },
            {
                label: t('testing'),
                icon: <SettingsOutlinedIcon />,
                link: `/${UI_FUNCTIONAL_TESTING_PATH}/`,
                permission: [
                    PERMISSIONS.VIEW_TESTCASES,
                    PERMISSIONS.VIEW_TESTPLANS,
                    PERMISSIONS.VIEW_TESTRUNS,
                    PERMISSIONS.VIEW_MODULES,
                ],
                testId: 'sidebar_testing',
                children: [
                    {
                        label: t('modules'),
                        icon: <GridViewOutlinedIcon />,
                        link: `/${UI_MODULES_PATH}`,
                        permission: PERMISSIONS.VIEW_MODULES,
                        testId: 'sidebar_modules',
                    },
                    {
                        label: t('test_cases'),
                        icon: <ScienceOutlinedIcon />,
                        link: `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_CASE_PATH}`,
                        permission: PERMISSIONS.VIEW_TESTCASES,
                        testId: 'sidebar_testcases',
                    },
                    {
                        label: t('test_plans'),
                        icon: <RuleOutlinedIcon />,
                        link: `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_PLAN_PATH}`,
                        permission: PERMISSIONS.VIEW_TESTPLANS,
                        testId: 'sidebar_testplans',
                    },
                    {
                        label: t('test_runs'),
                        icon: <SubscriptionsOutlinedIcon />,
                        link: `/${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}`,
                        permission: PERMISSIONS.VIEW_TESTRUNS,
                        testId: 'sidebar_testruns',
                    },
                    {
                        label: t('defects'),
                        icon: <BugReportIcon />,
                        link: `/${UI_DEFECTS_PATH}`,
                        permission: PERMISSIONS.VIEW_DEFECTS,
                        testId: 'sidebar_defects',
                    },
                ],
            },
            // TODO disable comments when performance section will be ready
            // {
            //     label: t('performance_testing'),
            //     icon: <PlaylistPlayOutlinedIcon />,
            //     children: [],
            // },
            {
                label: t('schemes'),
                icon: <HubOutlinedIcon />,
                link: `/${UI_SCHEMES_PATH}`,
                permission: PERMISSIONS.VIEW_SCHEMES,
                testId: 'sidebar_schemes',
            },
            {
                label: t('stands'),
                icon: <MonitorOutlinedIcon />,
                link: `/${UI_STANDS_PATH}`,
                permission: PERMISSIONS.VIEW_STANDS,
                testId: 'sidebar_stands',
            },
            {
                label: t('library'),
                icon: <FolderOpenIcon />,
                link: `/${UI_LIBRARY_PATH}`,
                permission: PERMISSIONS.VIEW_LIBRARY,
                testId: 'sidebar_library',
            },
            {
                label: t('booking'),
                icon: <EventAvailableOutlinedIcon />,
                link: `/${UI_BOOKING_PATH}`,
                permission: PERMISSIONS.VIEW_BOOKINGS,
                testId: 'sidebar_booking',
            },
        ];
    }, [t]);

    // sync stored collapsed state with visible
    useEffect(() => {
        setIsVisibleCollapsed(isCollapsed);
    }, [isCollapsed]);

    const toggleVisibleCollapsedState = (action) => {
        // if sidebar always needs be opened OR collapse is blocked, ignore changing collapsed state
        if (!isCollapsed || isSidebarCollapseBlocked) {
            return;
        }

        if (action === 'open') {
            clearTimeout(collapseTimeout.current);
            setIsVisibleCollapsed(false);
        } else if (action === 'close') {
            collapseTimeout.current = setTimeout(() => {
                setIsVisibleCollapsed(true);
            }, 200);
        }
    };

    return (
        <Box
            data-testid="sidebar"
            onMouseEnter={() => toggleVisibleCollapsedState('open')}
            onMouseLeave={() => toggleVisibleCollapsedState('close')}
            sx={{
                position: 'sticky',
                display: 'flex',
                marginTop: '48px',
                userSelect: 'none',
                zIndex: 100,
                width: isVisibleCollapsed || isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH,

                // webkit scrollbar styles
                '& ::-webkit-scrollbar-track': {
                    background: theme.palette.components.navigation.background,
                },
                '& ::-webkit-scrollbar-thumb': {
                    borderRadius: '6px',
                    background: 'transparent',
                },
                '& :hover ::-webkit-scrollbar-thumb': {
                    background: '#a3a6a9',
                },
                '& ::-webkit-scrollbar-thumb:hover ': {
                    background: '#86878a',
                },
            }}
        >
            <ProSidebar
                rootStyles={{
                    borderRight: 'none',
                    [`.${sidebarClasses.container}`]: {
                        padding: '24px 8px',
                        background: theme.palette.components.navigation.background,
                    },
                    transitionTimingFunction: 'cubic-bezier(0.32, 0, 0.67, 0)',
                }}
                collapsed={isVisibleCollapsed}
                collapsedWidth={`${COLLAPSED_SIDEBAR_WIDTH}px`}
                width={`${EXPANDED_SIDEBAR_WIDTH}px`}
                backgroundColor={theme.palette.components.navigation.background}
                transitionDuration={200}
            >
                <Menu
                    rootStyles={{
                        [`.${menuClasses.subMenuContent} `]: {
                            background: theme.palette.components.navigation.background,
                        },
                        [`.${menuClasses.subMenuRoot}`]: {
                            background: theme.palette.components.navigation.background,
                        },
                        [`.${menuClasses.button}`]: {
                            cursor: 'pointer',
                        },
                        [`.${menuClasses.button}:hover`]: {
                            background: theme.palette.primary.hover,
                            borderRadius: '32px',
                        },
                        [`.${menuClasses.menuItemRoot}`]: {
                            background: theme.palette.components.navigation.background,
                        },
                        [`.${menuClasses.icon}.${menuClasses.active}`]: {
                            color: theme.palette.primary.main,
                        },
                    }}
                    menuItemStyles={{
                        button: {
                            [`&.ps-active`]: {
                                background: theme.palette.primary.selected,
                                borderRadius: '32px',
                                fontWeight: 700,
                            },
                            paddingRight: '16px',
                            paddingLeft: '16px !important',
                        },
                        icon: {
                            marginRight: '16px',
                            justifyContent: 'left',
                            width: 'fit-content',
                            minWidth: 'fit-content',
                        },
                        SubMenuExpandIcon: {
                            display: 'none',
                        },
                        subMenuContent: {
                            paddingLeft: '32px',
                        },
                    }}
                >
                    <SidebarRecursiveMenu menuItems={menuItems} sidebarIsCollapsed={isVisibleCollapsed} />
                </Menu>
            </ProSidebar>
        </Box>
    );
};

export default Sidebar;
